import React from "react";
import ContentLoader from "react-content-loader";
import {
  FiTruck,
  FiUsers,
  FiPackage,
  FiTrendingUp,
  FiBarChart2,
  FiMapPin,
  FiDollarSign,
} from "react-icons/fi";
import { GoLocation } from "react-icons/go";

const SuppliersStatsLoadingSkeleton = ({ isDark = true }) => {
  const backgroundColor = isDark ? "#1a1a1a" : "#f0f0f0";
  const foregroundColor = isDark ? "#2a2a2a" : "#e5e7eb";

  return (
    <div className="stats-tab">
      {/* KPIs Estratégicos */}
      <div className="stats-kpi-grid">
        {[
          { icon: FiTruck, label: "Volume Total de Compras", badge: "SPED EFD", className: "primary" },
          { icon: FiUsers, label: "Fornecedores Ativos", badge: "ATIVOS", className: "success" },
          { icon: FiPackage, label: "Ticket Médio por Fornecedor", badge: "TICKET", className: "warning" },
          { icon: GoLocation, label: "Estados com Fornecedores", badge: "UFs", className: "info" },
        ].map((item, i) => (
          <div key={`kpi-${i}`} className={`stats-kpi-card ${item.className}`}>
            <div className="kpi-header">
              <item.icon className="kpi-icon" />
              <span className="kpi-badge">{item.badge}</span>
            </div>
            <div className="kpi-value">
              <ContentLoader
                speed={2}
                width={150}
                height={28}
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
              >
                <rect x="0" y="0" rx="4" ry="4" width="150" height="28" />
              </ContentLoader>
            </div>
            <div className="kpi-label">{item.label}</div>
            <div className="kpi-trend positive">
              <ContentLoader
                speed={2}
                width={120}
                height={12}
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
              >
                <rect x="0" y="0" rx="3" ry="3" width="120" height="12" />
              </ContentLoader>
            </div>
          </div>
        ))}
      </div>

      {/* Linha 1 - Distribuição Geográfica */}
      <div className="stats-row" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "20px" }}>
        {/* Top Estados */}
        <div className="stats-card top-estados">
          <div className="card-header">
            <div className="header-title">
              <GoLocation />
              <h4>Top Estados</h4>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              padding: "12px",
              overflowY: "auto",
              paddingRight: "4px",
            }}
            className="custom-scrollbar"
          >
            {[...Array(8)].map((_, i) => {
              const colors = ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#64748b"];
              const color = colors[i % colors.length];
              return (
                <div
                  key={`state-${i}`}
                  style={{
                    background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
                    borderRadius: "10px",
                    padding: "12px 14px",
                    border: `1px solid ${color}20`,
                    position: "relative",
                    overflow: "hidden",
                    minHeight: "52px",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: `${100 - i * 10}%`,
                      background: `linear-gradient(90deg, ${color}15 0%, ${color}05 100%)`,
                      borderRadius: "10px",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          background: i < 3 ? `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` : "rgba(100, 116, 139, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75em",
                          fontWeight: 700,
                          color: i < 3 ? "#fff" : "#94a3b8",
                        }}
                      >
                        {i + 1}
                      </div>
                      <div>
                        <ContentLoader
                          speed={2}
                          width={120}
                          height={11}
                          backgroundColor={backgroundColor}
                          foregroundColor={foregroundColor}
                        >
                          <rect x="0" y="0" rx="3" ry="3" width="120" height="11" />
                        </ContentLoader>
                        <div style={{ marginTop: "4px" }}>
                          <ContentLoader
                            speed={2}
                            width={80}
                            height={9}
                            backgroundColor={backgroundColor}
                            foregroundColor={foregroundColor}
                          >
                            <rect x="0" y="0" rx="3" ry="3" width="80" height="9" />
                          </ContentLoader>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <ContentLoader
                        speed={2}
                        width={90}
                        height={13}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="90" height="13" />
                      </ContentLoader>
                      <div style={{ marginTop: "4px" }}>
                        <ContentLoader
                          speed={2}
                          width={50}
                          height={9}
                          backgroundColor={backgroundColor}
                          foregroundColor={foregroundColor}
                        >
                          <rect x="0" y="0" rx="3" ry="3" width="50" height="9" />
                        </ContentLoader>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mapa do Brasil */}
        <div className="stats-card">
          <div className="card-header">
            <div className="header-title">
              <GoLocation />
              <h4>Mapa do Brasil</h4>
            </div>
          </div>
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center", 
            padding: "24px", 
            flex: 1, 
            minHeight: "420px" 
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              border: "4px solid rgba(139, 92, 246, 0.2)",
              borderTop: "4px solid #8b5cf6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}></div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      </div>

      {/* Linha 2 - Top Fornecedores e NCM */}
      <div className="stats-row">
        {/* Top Fornecedores */}
        <div className="stats-card top-suppliers">
          <div className="card-header">
            <div className="header-title">
              <FiTruck />
              <h4>Top 5 Fornecedores por Volume</h4>
            </div>
            <span className="header-badge">Ranking</span>
          </div>
          <div className="top-list">
            {[...Array(5)].map((_, i) => (
              <div key={`supplier-${i}`} className="top-item">
                <div className="rank-badge">#{i + 1}</div>
                <div className="supplier-info">
                  <div className="supplier-name">
                    <ContentLoader
                      speed={2}
                      width={200}
                      height={14}
                      backgroundColor={backgroundColor}
                      foregroundColor={foregroundColor}
                    >
                      <rect x="0" y="0" rx="3" ry="3" width="200" height="14" />
                    </ContentLoader>
                  </div>
                  <div className="supplier-meta">
                    <span className="cnpj">
                      <ContentLoader
                        speed={2}
                        width={120}
                        height={11}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="120" height="11" />
                      </ContentLoader>
                    </span>
                    <span className="category">
                      <ContentLoader
                        speed={2}
                        width={80}
                        height={11}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="80" height="11" />
                      </ContentLoader>
                    </span>
                  </div>
                </div>
                <div className="supplier-stats">
                  <div className="stat-value">
                    <ContentLoader
                      speed={2}
                      width={100}
                      height={14}
                      backgroundColor={backgroundColor}
                      foregroundColor={foregroundColor}
                    >
                      <rect x="0" y="0" rx="3" ry="3" width="100" height="14" />
                    </ContentLoader>
                  </div>
                  <div className="stat-bar">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${100 - i * 18}%`,
                        background: i === 0 ? "#10b981" : i === 1 ? "#3b82f6" : "#8b5cf6",
                      }}
                    />
                  </div>
                  <div className="stat-percent">
                    <ContentLoader
                      speed={2}
                      width={40}
                      height={11}
                      backgroundColor={backgroundColor}
                      foregroundColor={foregroundColor}
                    >
                      <rect x="0" y="0" rx="3" ry="3" width="40" height="11" />
                    </ContentLoader>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Análise por NCM */}
        <div className="stats-card ncm-analysis">
          <div className="card-header">
            <div className="header-title">
              <FiPackage />
              <h4>Principais NCMs Adquiridos</h4>
            </div>
            <span className="header-badge">Classificação Fiscal</span>
          </div>
          <div className="ncm-list-pro">
            {[...Array(5)].map((_, i) => (
              <div key={`ncm-${i}`} className="ncm-item-pro">
                <div className="ncm-rank-pro">{i + 1}</div>
                <div className="ncm-main">
                  <div className="ncm-info-pro">
                    <span className="ncm-code-pro">
                      <ContentLoader
                        speed={2}
                        width={80}
                        height={12}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="80" height="12" />
                      </ContentLoader>
                    </span>
                    <span className="ncm-desc-pro">
                      <ContentLoader
                        speed={2}
                        width={200}
                        height={11}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="200" height="11" />
                      </ContentLoader>
                    </span>
                  </div>
                  <div className="ncm-bar-pro">
                    <div
                      className="bar-fill-pro"
                      style={{ width: `${100 - i * 18}%` }}
                    />
                  </div>
                </div>
                <div className="ncm-values-pro">
                  <div className="ncm-amount">
                    <ContentLoader
                      speed={2}
                      width={90}
                      height={13}
                      backgroundColor={backgroundColor}
                      foregroundColor={foregroundColor}
                    >
                      <rect x="0" y="0" rx="3" ry="3" width="90" height="13" />
                    </ContentLoader>
                  </div>
                  <div className="ncm-details">
                    <span className="ncm-pct">
                      <ContentLoader
                        speed={2}
                        width={40}
                        height={10}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="40" height="10" />
                      </ContentLoader>
                    </span>
                    <span className="ncm-ipi">
                      <ContentLoader
                        speed={2}
                        width={50}
                        height={10}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="50" height="10" />
                      </ContentLoader>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Linha 3 - Distribuição de Impostos */}
      <div className="stats-row">
        <div className="stats-card category-analysis">
          <div className="card-header">
            <div className="header-title">
              <FiDollarSign />
              <h4>Distribuição de Impostos</h4>
            </div>
            <span className="header-badge">SPED Fiscal</span>
          </div>
          <div className="category-list">
            {[...Array(5)].map((_, i) => {
              const colors = ["#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444"];
              return (
                <div key={`tax-${i}`} className="category-item">
                  <div className="category-header">
                    <div className="category-color" style={{ background: colors[i] }} />
                    <span className="category-name">
                      <ContentLoader
                        speed={2}
                        width={100}
                        height={12}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="100" height="12" />
                      </ContentLoader>
                    </span>
                    <span className="category-value">
                      <ContentLoader
                        speed={2}
                        width={90}
                        height={12}
                        backgroundColor={backgroundColor}
                        foregroundColor={foregroundColor}
                      >
                        <rect x="0" y="0" rx="3" ry="3" width="90" height="12" />
                      </ContentLoader>
                    </span>
                  </div>
                  <div className="category-bar">
                    <div
                      className="category-progress"
                      style={{
                        width: `${100 - i * 18}%`,
                        background: colors[i],
                      }}
                    />
                  </div>
                  <div className="category-desc">
                    <ContentLoader
                      speed={2}
                      width={200}
                      height={10}
                      backgroundColor={backgroundColor}
                      foregroundColor={foregroundColor}
                    >
                      <rect x="0" y="0" rx="3" ry="3" width="200" height="10" />
                    </ContentLoader>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuppliersStatsLoadingSkeleton;
